import React, { useCallback, useState } from 'react';
import { AutocompleteRenderInputParams, TextField } from '@mui/material';
import debounce from 'lodash/debounce';

import { FormInputProps } from '@occc-app/design-system';

import { AutocompleteOptionType, UseLazyAutocompleteLogicParams } from './types';

export const useAutoCompleteInputValue = (initialValue = '') => {
  const [inputValue, setInputValue] = useState<string>(initialValue);
  const onInputChange = React.useCallback((event: React.ChangeEvent, selectedValue: string) => {
    setInputValue(selectedValue);
  }, []);

  return {
    inputValue,
    onInputChange,
  };
};

// TODO: figure out a better name and refactor this
type AutoCompleteRenderInputMeta = {
  label: string;
  placeholder?: string; // 'Start typing to select tag';
  errorText: string;
  inputProps?: FormInputProps;
};

export const renderAutocompleteInput = (
  params: AutocompleteRenderInputParams,
  meta: AutoCompleteRenderInputMeta,
): React.ReactNode => {
  const { label, placeholder, errorText, inputProps } = meta;
  return (
    <TextField
      {...params}
      placeholder={placeholder}
      helperText={errorText}
      error={!!errorText}
      fullWidth={true}
      variant='outlined'
      margin={inputProps?.margin ?? 'normal'}
      label={label}
      sx={{
        '& input': {
          backgroundColor: 'inherit', // One-off: Prevents default gray background when parent component is in 'disabled' status
        },
      }}
      {...inputProps}
    />
  );
};

export const mapAutocompleteOptionToRawResponseItem = (option: AutocompleteOptionType) =>
  option._raw;

export const useLazyAutocompleteLogic = ({
  makeQueryFilter,
  mapRawResponseItemToAutocompleteOption,
  mapQueryResponseToRawArray,
  useLazyQuery,
  placeholder,
  label,
  errorText,
  inputProps,
  first,
}: UseLazyAutocompleteLogicParams<any, any>) => {
  const { inputValue, onInputChange } = useAutoCompleteInputValue('');

  /**
   * I think this is not the most effective approach, but it works for now.
   * It should be refactored later to avoid unnecessary re-renders and adding context that way.
   */
  const renderInput = useCallback(
    (params: AutocompleteRenderInputParams) => {
      return renderAutocompleteInput(params, {
        label,
        placeholder: placeholder || 'Start typing to select tag',
        errorText: errorText || '',
        inputProps,
      });
    },
    [errorText, inputProps, label, placeholder],
  );

  const { executeQuery, response } = useLazyQuery();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const executeQueryDebounced = React.useMemo(() => debounce(executeQuery, 700), [useLazyQuery]);
  React.useEffect(() => {
    executeQueryDebounced({
      variables: {
        filter: makeQueryFilter ? makeQueryFilter(inputValue) : undefined,
        first: first || 10,
      },
    });
  }, [makeQueryFilter, inputValue, executeQueryDebounced, first]);

  const responseRawArray = mapQueryResponseToRawArray(response);
  const responseOptionsArray = responseRawArray.map(mapRawResponseItemToAutocompleteOption);

  return {
    renderInput,
    loading: response.loading,
    inputValue,
    onInputChange,
    responseOptionsArray,
    hasFilterOptions: !makeQueryFilter,
  };
};
